// Imports
// *****************************************************
import { Fragment, MouseEvent } from 'react';
import { useTranslation } from 'next-i18next';
import {
  Anchor,
  Button,
  LinkStyled,
  Dropdown,
  DropdownButton,
  DropdownItems,
  DropdownItem,
} from '@components/common';
import { BriefcaseIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { i18nConfig } from 'i18n-config';
import { getI18nLinkHref } from '@services/i18nPaths';
import { isHireRoute } from '@lib/functions/routes/isHireRoute';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import { mainMenuLinks } from './links-objects';

/**
 * Props for the MobileHireMenu component.
 */
interface MobileHireMenuProps {
  /** User role ('NONE' | 'USER' | 'COMPANY'); undefined/null means the user is not authenticated. */
  role?: string | null,
  /** Current route pathname — used to highlight the active link and pick the HIRE/TALENT section. */
  currentRoute?: string,
  /** Current app locale. */
  locale?: Locale,
}

/**
 * @description Mobile-only "Hire" menu. Renders the same links as the desktop MainMenuLinks hire
 * dropdown, but as a compact top-bar button that opens a downward popup. Visibility mirrors the
 * desktop rule (section + role filtering on `mainMenuLinks`).
 */
export const MobileHireMenu = (props: MobileHireMenuProps) => {
  // Destructure props
  const {
    role,
    currentRoute = '',
    locale,
  } = props;

  // Translate
  const { t } = useTranslation('common');

  const headerInfo = useAppSelector(getUserHeaderInfo);
  const headerInfoExtended = headerInfo as ApiSchema<'HeaderInfo'> & { discountPercentage?: number };

  // Same section + role filtering as desktop MainMenuLinks
  // *****************************************************
  let menusToShow = mainMenuLinks;
  if (role == null) {
    menusToShow = mainMenuLinks.filter((link) => link.onlyLoggedIn === false || link.onlyLoggedIn === undefined);
  }
  const hireMenu = menusToShow
    .filter((item) => (isHireRoute(currentRoute) ? item.forSection?.includes('HIRE') : item.forSection?.includes('TALENT')))
    .find((item) => item.children);

  // Nothing to show for this section/role — render nothing.
  if (!hireMenu?.children) return null;

  const callIfTrusted = (event: MouseEvent<HTMLElement>, callback?: () => void) => {
    if (!callback) return;
    if (!event.nativeEvent.isTrusted) return;
    callback();
  };

  const isActiveRoute = (linkRoute: string) => {
    if (!currentRoute) return false;

    const pathSegments = currentRoute.split('/').filter(Boolean);
    const currentLocale = i18nConfig.locales.includes(pathSegments[0] as Locale) ? (pathSegments[0] as Locale) : null;
    const basePath = currentLocale ? currentRoute.replace(`/${currentLocale}`, '') : currentRoute;

    const translatedNoPrefix = getI18nLinkHref(linkRoute, locale ?? 'ro', false);
    return basePath.startsWith(translatedNoPrefix);
  };

  // Render component
  // *****************************************************
  return (
    <Dropdown>
      <DropdownButton
        as={Button}
        styling="none"
        className="inline-flex cursor-pointer items-center px-1 py-2.5 outline-none hover:text-primary"
        title={t(hireMenu.label)}
        data-test-id="navbar-hire-mobile"
      >
        <BriefcaseIcon className="size-5 stroke-2" />
      </DropdownButton>

      <DropdownItems
        animation="slideDown"
        anchor={{ to: 'bottom end', gap: '0.75rem' }}
        className="min-w-40 border-t border-input/40"
      >
        {hireMenu.children.map((item) => {
          // This popup mirrors the desktop dropdown, so use the full-text entries and skip the
          // icon-only mobile variants (e.g. the phone-support item that has no label).
          if (item.onlyMobile) return (<Fragment key={item.label || item.route} />);

          // If onClick exists but no route (e.g., route is '#'), render as button only
          if (item.onClick && item.route === '#') {
            return (
              <DropdownItem
                key={item.label || item.dataTestId}
                as="button"
                onClick={(event: MouseEvent<HTMLElement>) => callIfTrusted(event, item.onClick)}
                className="flex w-full items-center gap-2 text-nowrap px-4 py-3 text-left text-sm outline-none transition hover:bg-surface"
                {...(item.dataTestId ? { 'data-test-id': item.dataTestId } : {})}
              >
                {item.icon && <item.icon className="size-4" />}
                {item.label && t(item.label)}
              </DropdownItem>
            );
          }

          const Component = item.type === 'react' ? LinkStyled : Anchor;
          const isSymfony = item.type === 'symfony';
          const extraProps = isSymfony ? { symfonyRoute: true } : {};

          return (
            <DropdownItem
              key={item.label || item.route}
              as={Component}
              href={item.route}
              styling="none"
              className={clsx(
                'flex w-full items-center gap-2 text-nowrap px-4 py-3 text-left text-sm outline-none transition hover:bg-surface',
                !isSymfony && isActiveRoute(item.route) && 'text-secondary',
              )}
              {...extraProps}
              {...(item.onClick ? { onClick: (event: MouseEvent<HTMLElement>) => callIfTrusted(event, item.onClick) } : {})}
              {...(item.dataTestId ? { 'data-test-id': item.dataTestId } : {})}
              {...(item.type === 'react' ? { locale } : {})}
            >
              {item.icon && <item.icon className="size-4" />}
              {(item.hasDiscount && headerInfoExtended?.discountPercentage)
                ? (
                  <div className="flex w-full items-center justify-between">
                    {t(item.label)}
                    <span className="ml-1 rounded-md bg-secondary/20 px-1 text-pico font-thin text-secondary">
                      {headerInfoExtended?.discountPercentage}
                      % OFF
                    </span>
                  </div>
                ) : t(item.label)}
            </DropdownItem>
          );
        })}
      </DropdownItems>
    </Dropdown>
  );
};
